import { useRef } from 'react';
import { SnackbarProvider } from 'notistack';
//
import Iconify from './Iconify';
import { IconButtonAnimate } from './animate';

// Provider de snackbar com estilos globais injetados como <style>.
// Cada notificacao pode ter uma cor (info, success, warning, error) que vem
// dos tokens CSS --gps-*.

function SnackbarStyles() {
  return (
    <style>{`
      /* ======================================================================
         AS NOTIFICACOES NUNCA RECEBERAM ESTE ESTILO (corrigido em 10/09/2026).

         Todo seletor daqui comecava com \`#__next\` - o id que o NEXT usa. Esta
         aplicacao roda em Vite e monta em \`#root\` (index.html:16), entao
         NENHUMA destas regras casava: o que aparecia na tela era o verde e o
         azul padrao do notistack, que e exatamente o que o usuario reclamou.
         Sobra da migracao, do mesmo tipo da Manrope declarada e nunca baixada.

         Os dois ids ficam na lista: \`#root\` para o Vite, \`#__next\` enquanto o
         Next existir em paralelo. O id e necessario pela ESPECIFICIDADE - o
         notistack pinta o fundo pelas proprias classes, e sem ele estas regras
         perdem a disputa.
         ====================================================================== */

      #root .SnackbarContent-root,
      #__next .SnackbarContent-root {
        width: 100%;
        min-height: 0;
        padding: 10px 12px;
        margin: 3px 0;
        gap: 10px;
        border-radius: 12px;
        /* Superficie do tema, e nao um bloco de cor. A cor entra na barra
           lateral e no icone: a mensagem e texto para ler, e texto branco sobre
           verde saturado se le pior que texto escuro sobre superficie. */
        color: var(--gps-text);
        background-color: var(--gps-surface);
        border: 1px solid var(--gps-line);
        box-shadow: 0 1px 2px rgb(16 24 40 / 0.06), 0 12px 32px -12px rgb(16 24 40 / 0.28);
      }

      /* A FAIXA DE COR e um pseudo-elemento, nao um \`border-left\`: borda
         participa da largura e desalinharia o conteudo das quatro variantes
         entre si. */
      #root .SnackbarContent-root::before,
      #__next .SnackbarContent-root::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 4px;
        border-radius: 12px 0 0 12px;
        background: var(--gps-muted);
      }

      #root .SnackbarContent-root,
      #__next .SnackbarContent-root {
        position: relative;
        overflow: hidden;
      }

      #root .SnackbarItem-variantSuccess::before,
      #__next .SnackbarItem-variantSuccess::before { background: var(--gps-ok); }
      #root .SnackbarItem-variantError::before,
      #__next .SnackbarItem-variantError::before { background: var(--gps-danger); }
      #root .SnackbarItem-variantWarning::before,
      #__next .SnackbarItem-variantWarning::before { background: var(--gps-warn); }
      #root .SnackbarItem-variantInfo::before,
      #__next .SnackbarItem-variantInfo::before { background: var(--gps-info); }

      /* As quatro variantes usam a MESMA superficie - o que muda entre elas e a
         faixa e o icone. Sem isto o notistack repinta o fundo de verde/vermelho
         por conta propria. */
      #root .SnackbarItem-variantSuccess,
      #root .SnackbarItem-variantError,
      #root .SnackbarItem-variantWarning,
      #root .SnackbarItem-variantInfo,
      #__next .SnackbarItem-variantSuccess,
      #__next .SnackbarItem-variantError,
      #__next .SnackbarItem-variantWarning,
      #__next .SnackbarItem-variantInfo {
        color: var(--gps-text);
        background-color: var(--gps-surface);
      }

      @media (min-width: 768px) {
        #root .SnackbarContent-root,
        #__next .SnackbarContent-root {
          min-width: 300px;
          max-width: 420px;
        }
      }

      #root .SnackbarItem-message,
      #__next .SnackbarItem-message {
        padding: 0 !important;
        gap: 10px;
        font-size: 12.5px;
        font-weight: 600;
        line-height: 1.35;
      }

      #root .SnackbarItem-action,
      #__next .SnackbarItem-action {
        margin-right: -2px;
        padding-left: 4px;
        color: var(--gps-faint);
      }

      #root .SnackbarItem-action:hover,
      #__next .SnackbarItem-action:hover { color: var(--gps-text); }

      #root .SnackbarItem-action svg,
      #__next .SnackbarItem-action svg {
        width: 16px;
        height: 16px;
      }
    `}</style>
  );
}

export default function NotistackProvider({ children }) {
  const notistackRef = useRef(null);

  const onClose = (key) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  return (
    <>
      <SnackbarStyles />

      <SnackbarProvider
        ref={notistackRef}
        dense
        maxSnack={5}
        preventDuplicate
        autoHideDuration={4000}
        variant="success"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        iconVariant={{
          info: <SnackbarIcon icon="eva:info-fill" color="info" />,
          success: <SnackbarIcon icon="eva:checkmark-circle-2-fill" color="ok" />,
          warning: <SnackbarIcon icon="eva:alert-triangle-fill" color="warn" />,
          error: <SnackbarIcon icon="eva:alert-circle-fill" color="danger" />,
        }}
        action={(key) => (
          <IconButtonAnimate
            size="small"
            onClick={onClose(key)}
            className="p-0.5"
          >
            <Iconify icon="eva:close-fill" />
          </IconButtonAnimate>
        )}
      >
        {children}
      </SnackbarProvider>
    </>
  );
}

function SnackbarIcon({ icon, color }) {
  // Mapeamento de cores MUI para tokens GPS
  const colorMap = {
    info: '--gps-info',
    ok: '--gps-ok',
    success: '--gps-ok',
    warn: '--gps-warn',
    warning: '--gps-warn',
    error: '--gps-danger',
  };

  const colorVar = colorMap[color] || '--gps-accent';
  const bgColorVar = colorMap[color] ? colorMap[color].replace('--gps-', '--gps-') + '-soft' : '--gps-accent-soft';

  return (
    /* 28px e nao 40: num aviso de UMA linha, um quadrado de 40px obriga a
       caixa inteira a ter 56px de altura para nada. */
    <span
      className="flex h-7 w-7 flex-none items-center justify-center rounded-lg"
      style={{
        color: `var(${colorVar})`,
        backgroundColor: `var(${bgColorVar})`,
      }}
    >
      <Iconify icon={icon} width={17} height={17} />
    </span>
  );
}
